import classNames from 'classnames';
import fragment from './ActivityTotalsFragment.gql';
import PageSection from '../PageSection/PageSection';
import React, { FunctionComponent, useState } from 'react';
import styles from './ActivityTotals.scss';
import { ActionLinks } from './ActionLinks/ActionLinks';
import { ExtraMeasurements } from './ExtraMeasurements/ExtraMeasurements';
import { FilterButtons } from './FilterButtons/FilterButtons';
import { FormattedDistance } from '../Formatters/FormattedDistance';
import { getMeasurementSystemFromSettings } from '../../utils/measurementSystem';
import { ProgressBar } from './ProgressBar/ProgressBar';
import { useFlags } from '../../hooks/useFlags';
import { useTranslation } from 'react-i18next';
import {
  ActivityTotalsFragment,
  PageTotal,
} from '../../../types/__generated__';
import usePageQuery, {
  buildQuery,
  UsePageQueryResult,
} from '../../hooks/usePageQuery';
import { ActivityFilters, buildFilteredTotals } from './filters';

const FRAGMENT = buildQuery(fragment);

export const AnyDistance = 'any:distance';

export type ActivityTotalsFlags = {
  enableActivityTotals: boolean;
  enableActivityTotalsExtras: boolean;
};

export const ActivityTotals: FunctionComponent = () => {
  let [activityFilter, setActivityFilter] = useState(ActivityFilters.all.key);
  const { t, i18n } = useTranslation('common', { useSuspense: false });

  const { enableActivityTotals, enableActivityTotalsExtras } = useFlags<
    ActivityTotalsFlags
  >();
  if (!enableActivityTotals) {
    return null;
  }

  // Avoid querying GraphQL if feature flag is disabled.
  // This flag is configured only per environment, so it's safe to use usePageQuery conditionally.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const result = usePageQuery<ActivityTotalsFragment>(FRAGMENT, {
    notifyOnNetworkStatusChange: true,
  });

  if (!UsePageQueryResult.hasData(result)) {
    return null;
  }

  const settings = result.data?.application?.settings;
  if (!settings) {
    return null;
  }

  const measurementSystem = getMeasurementSystemFromSettings(i18n, settings);

  const timelineTotals: PageTotal[] = result.data?.totals?.timeline ?? [];
  const anyDistance = timelineTotals.find(
    t => t.measurementDomain === AnyDistance,
  );

  if (!anyDistance) {
    return null;
  }

  const {
    activityTotalsMap,
    activityTypes,
    isSingleActivityMode,
  } = buildFilteredTotals(measurementSystem, timelineTotals);

  if (isSingleActivityMode && activityFilter === ActivityFilters.all.key) {
    // The initial value of activityFilter is "all".
    // However, we remove "all" if there is only one activity type.
    // So, switch the current filter over to that one type.
    setActivityFilter(activityTypes[0]);
  }

  const totals = activityTotalsMap[activityFilter];

  return (
    <PageSection className={styles.activityTotalsSection}>
      <div className={styles.box}>
        <FilterButtons
          activityTypes={activityTypes}
          activityFilter={activityFilter}
          setActivityFilter={setActivityFilter}
        />
        <div className={classNames('jg-text--regular-light', styles.summary)}>
          <strong>
            <FormattedDistance value={totals.distance} />
          </strong>
          {` ${t('activity.totals.completedSoFar')}`}
        </div>
        <ProgressBar
          percent={enableActivityTotalsExtras ? 55 : 0}
        ></ProgressBar>
        {enableActivityTotalsExtras && (
          <>
            <ExtraMeasurements
              duration={totals.duration}
              elevation={totals.elevation}
            />
            <ActionLinks />
          </>
        )}
      </div>
    </PageSection>
  );
};
